import { ValidationStatus } from '@onwardtravelsolutions/package-react/Enums';
import { useProgress } from '@onwardtravelsolutions/package-react/Hooks';
import { Notice } from '@onwardtravelsolutions/package-react/Types';
import { useEffect } from 'react';
import ValidationList from './ValidationList';

type FieldValidationProps = {
    id: string;
    status?: ValidationStatus;
    children: React.ReactNode;
    className?: string;
    duration?: number;
    notices?: Notice[];
    showNotices?: boolean;
    name?: string;
};

export default function FieldValidation({
    id,
    status = ValidationStatus.Unknown,
    children,
    className = '',
    notices = [],
    duration = 600,
    showNotices = false,
    name = null,
}: FieldValidationProps) {
    const validationDuration = Math.min(10000, Math.max(1, duration));
    const { percentage, start, reset } = useProgress(validationDuration, false);

    useEffect(() => {
        if (status === ValidationStatus.Pending) {
            start();
        } else {
            reset();
        }
    }, [status]);

    const background =
        {
            [ValidationStatus.Unknown]: 'bg-gray-200 dark:bg-neutral-500',
            [ValidationStatus.Hard]: 'bg-error',
            [ValidationStatus.Soft]: 'bg-orange-200',
            [ValidationStatus.Valid]: 'bg-green-400',
            [ValidationStatus.Pending]: 'bg-blue-200 dark:bg-blue-400',
        }[status] ?? 'bg-gray-200 dark:bg-neutral-500';

    return (
        <div className={`${className} relative`} id={`field-validation-${id}`}>
            <div
                className={`pointer-events-none absolute h-1 rounded-b z-10 bg-gray-200 dark:bg-neutral-500 overflow-none bottom-0 left-0 w-full`}
            >
                <div
                    className={`pointer-events-none relative rounded-b block h-1 z-20 ${background}`}
                    style={{
                        width:
                            status === ValidationStatus.Pending
                                ? `${percentage}%`
                                : 'auto',
                    }}
                ></div>
            </div>
            {children}
            {showNotices && notices.length ? (
                <ValidationList
                    {...{
                        fieldName: name,
                        notices: notices,
                        className:
                            'absolute w-full top-full drop-shadow-md dark:drop-shadow-lg z-30 m-0 -mt-px',
                    }}
                />
            ) : null}
        </div>
    );
}
